import type { GameEvent } from '../engine/game';

interface Beep {
  freq: number;
  dur: number;
}

// Ereignis → Beep (Frequenz Hz, Dauer s) laut Spec §6.
const START: Beep = { freq: 660, dur: 0.08 };
const ROTATE: Beep = { freq: 440, dur: 0.03 };
const LOCK: Beep = { freq: 200, dur: 0.05 };
const LINES_LOW: Beep = { freq: 780, dur: 0.14 };
const LINES_TETRIS: Beep = { freq: 980, dur: 0.14 };
const GAMEOVER: Beep = { freq: 120, dur: 0.4 };

const GAIN = 0.04;

/** Square-Wave-Beeps über WebAudio. AudioContext erst bei erster Interaktion. */
export class Sound {
  private ctx: AudioContext | null = null;
  private muted: boolean;

  constructor(muted = false) {
    this.muted = muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** Bei erster Nutzerinteraktion aufrufen (iOS entsperrt den Context). */
  unlock(): void {
    this.ensureCtx();
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private ensureCtx(): void {
    if (this.ctx) return;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor) this.ctx = new Ctor();
  }

  private play({ freq, dur }: Beep): void {
    if (this.muted) return;
    this.ensureCtx();
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = freq;
      gain.gain.value = GAIN;
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + dur);
    } catch {
      /* Audio nicht verfügbar — still ignorieren. */
    }
  }

  /** Reagiert auf ein Spielereignis mit dem passenden Beep. */
  handle(e: GameEvent): void {
    switch (e.type) {
      case 'start':
        this.play(START);
        break;
      case 'rotate':
        this.play(ROTATE);
        break;
      case 'lock':
        this.play(LOCK);
        break;
      case 'lines':
        this.play(e.count >= 4 ? LINES_TETRIS : LINES_LOW);
        break;
      case 'gameover':
        this.play(GAMEOVER);
        break;
      default:
        break;
    }
  }
}
